import {
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  Badge,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import FormModal from '../../../components/FormModal';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { CouponDto, useListCoupons } from '../../coupon-management/hooks/useListCoupons';
import { useAssignCoupon } from '../../coupon-management/hooks/useAssignCoupon';

interface AssignCouponToCohortModalProps {
  cohortId: string;
  cohortName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function AssignCouponToCohortModal({
  cohortId,
  cohortName,
  isOpen,
  onClose,
}: AssignCouponToCohortModalProps) {
  const toast = useToast();
  const { selectedOrganization } = useSelectedOrganization();
  // Without `isError`, a refused read told the operator "no active coupons" —
  // a confident claim about the org, made while they were mid-assignment.
  // See `coupon-read-error-gate.test.ts`.
  //
  // R118-2 — same two-term arithmetic as its user-facing twin (D72). The only
  // caller, `CohortDetailPage`, is gated on `cohortId` from the route and reads
  // no organization at all, so with nothing selected this dialog opened onto a
  // `useListCoupons` that was never issued — and a DISABLED React Query v4 read
  // reports `isLoading: true` with `fetchStatus: 'idle'`, so the raw flag below
  // rendered a PERMANENT "Loading…". `isInitialLoading` is false there.
  const {
    data: coupons,
    isInitialLoading: couponsLoading,
    isError: couponsError,
  } = useListCoupons(selectedOrganization?.id || '');
  const couponsReadState = classifyRead(couponsError, couponsLoading, coupons);
  const assignCoupon = useAssignCoupon();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCouponIds, setSelectedCouponIds] = useState<string[]>([]);

  // Filter coupons based on search query
  const filteredCoupons = useMemo(() => {
    if (!coupons) return [];
    if (!searchQuery.trim()) return coupons;

    const query = searchQuery.toLowerCase();
    return coupons.filter((coupon) =>
      coupon.name.toLowerCase().includes(query),
    );
  }, [coupons, searchQuery]);

  // Filter out expired coupons
  const activeCoupons = useMemo(() => {
    return filteredCoupons.filter(
      (coupon) => new Date(coupon.expiresAt) > new Date(),
    );
  }, [filteredCoupons]);

  const handleToggleCoupon = (couponId: string) => {
    setSelectedCouponIds((prev) =>
      prev.includes(couponId)
        ? prev.filter((id) => id !== couponId)
        : [...prev, couponId],
    );
  };

  const handleToggleAll = () => {
    if (selectedCouponIds.length === activeCoupons.length) {
      setSelectedCouponIds([]);
    } else {
      setSelectedCouponIds(activeCoupons.map((c) => c.id));
    }
  };

  const handleAssign = async () => {
    if (selectedCouponIds.length === 0) {
      toast({
        title: translate('coupon.no_coupons_selected'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Assign each selected coupon to the cohort
      await Promise.all(
        selectedCouponIds.map((couponId) =>
          assignCoupon.mutateAsync({
            couponId,
            cohortId,
          }),
        ),
      );

      toast({
        title: translate('cohort.coupons_assigned_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onModalClose();
    } catch (error) {
      toast({
        title: translate('cohort.assign_coupons_failed'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onModalClose = () => {
    setSearchQuery('');
    setSelectedCouponIds([]);
    onClose();
  };

  const getDiscountBadge = (coupon: CouponDto) => {
    if (coupon.discountType === 'PERCENTAGE') {
      return (
        <Badge colorScheme="purple" ml={2}>
          -{coupon.discountValue}%
        </Badge>
      );
    }
    return (
      <Badge colorScheme="brand" ml={2}>
        -{coupon.discountValue} {(coupon.currency || '').toUpperCase()}
      </Badge>
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={`${translate('cohort.assign_coupons_to')} "${cohortName}"`}
      size="xl"
      onSubmit={handleAssign}
      submitLabel={translate('cohort.assign_coupons')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={assignCoupon.isLoading}
      isSubmitDisabled={selectedCouponIds.length === 0}
    >
      <FormControl mb={4}>
        <FormLabel>{translate('coupon.search_coupons')}</FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder={translate('coupon.search_by_name')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </FormControl>

      {couponsReadState === 'unreadable' ? (
        <InlineErrorState message={translate('common.unexpected_error')} />
      ) : couponsReadState === 'loading' ? (
        <Text color="gray.500" textAlign="center" py={8}>
          {translate('coupon.loading')}
        </Text>
      ) : couponsReadState === 'never-issued' ? (
        // A text line rather than the `ScopePrompt` panel: one control inside a
        // form (run 114's call). Borrowed key — the i18n catalogues are frozen
        // behind the open-PR fence (R96-7) and this sentence exists in both.
        <Text color="gray.500" textAlign="center" py={8}>
          {translate(scopePromptI18nKey('organization') as any)}
        </Text>
      ) : activeCoupons.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={8}>
          {searchQuery
            ? translate('coupon.no_coupons_found')
            : translate('coupon.no_active_coupons')}
        </Text>
      ) : (
        <>
          <FormControl mb={4}>
            <Checkbox
              isChecked={
                selectedCouponIds.length === activeCoupons.length &&
                activeCoupons.length > 0
              }
              onChange={handleToggleAll}
            >
              <Text fontWeight="bold">
                {translate('cohort.select_all')} ({activeCoupons.length})
              </Text>
            </Checkbox>
          </FormControl>

          <Stack spacing={2} maxH="400px" overflowY="auto">
            {activeCoupons.map((coupon) => (
              <Checkbox
                key={coupon.id}
                isChecked={selectedCouponIds.includes(coupon.id)}
                onChange={() => handleToggleCoupon(coupon.id)}
              >
                <HStack spacing={2}>
                  <Text>{coupon.name}</Text>
                  {getDiscountBadge(coupon)}
                  <Text fontSize="xs" color="gray.500">
                    ({translate('coupon.expires')}{' '}
                    {new Date(coupon.expiresAt).toLocaleDateString()})
                  </Text>
                </HStack>
              </Checkbox>
            ))}
          </Stack>

          <Text mt={4} color="gray.600" fontSize="sm">
            {selectedCouponIds.length} {translate('coupon.coupons_selected')}
          </Text>
        </>
      )}
    </FormModal>
  );
}
